/**
 * Municipal-limit / ETJ geographic screening.
 *
 * Legal boundary discipline:
 * - intersects published polygons; never buffers city limits to invent an ETJ;
 * - `outside-known-etj` requires registry-declared complete ETJ coverage;
 * - permitting authority always remains `not-evaluated`.
 */
import polygonClipping from 'polygon-clipping';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';
import { statePostalCodeForFips } from '../refdata/fipsLookup';
import {
  CENSUS_COUNTIES_LAYER,
  CENSUS_INCORPORATED_PLACES_LAYER,
  findJurisdictionRegistryCandidate,
  findJurisdictionRegistryEntry,
  type JurisdictionArcgisLayer,
  type JurisdictionArcgisPolygonSource,
  type JurisdictionRegistryEntry,
  type VerifiedJurisdictionFips,
} from '../../config/jurisdictionRegistry';
import { polygonArea } from '../../lib/siteBoundaryGeometry';
import type {
  JurisdictionEvidence,
  JurisdictionOverlapEvidence,
  JurisdictionRegistryApplicabilityEvidence,
  JurisdictionScreeningStatus,
  ParcelJurisdictionEvidence,
} from '../../types/siteEvidence';
import type {
  DataProvider,
  ProviderCacheKeyContext,
  ProviderGeoContext,
  ProviderResult,
} from './types';
import { boundaryFingerprint, CACHE_TTL, fetchWithTimeout } from './types';

type LatLng = { lat: number; lng: number };
type LngLat = [number, number];

interface ArcgisFeature {
  attributes: Record<string, unknown>;
  geometry: { rings: number[][][] };
}

export interface ResolvedJurisdictionFips extends VerifiedJurisdictionFips {
  blockFips: string;
  blockBbox: [number, number, number, number];
  countyName: string;
  stateCode: string;
}

/** County-only fallback never invents FCC block identity or extent. */
export interface ResolvedCensusCountyFips extends VerifiedJurisdictionFips {
  countyName: string;
  stateCode: string;
  sourceUrl: string;
  censusCounty: { rings: number[][][] };
}
export type ResolvedCountyJurisdictionFips = ResolvedJurisdictionFips | ResolvedCensusCountyFips;

export interface JurisdictionBoundaryFeature {
  id: string;
  name: string;
  kind: 'city-limit' | 'etj';
  geometry: polygonClipping.MultiPolygon;
}

interface ParcelInput {
  id: string;
  polygon: LatLng[];
}

const AREA_EPSILON_FRACTION = 0.001;
// County containment is intentionally much stricter than the 0.1% display
// overlap threshold: 0.0001 acre (~0.4 m²) plus 1e-8 relative tolerance only
// absorbs polygon-clipping floating-point dust, not a real cross-county sliver.
const COUNTY_CONTAINMENT_ABSOLUTE_TOLERANCE_ACRES = 0.0001;
const COUNTY_CONTAINMENT_RELATIVE_TOLERANCE = 1e-8;
// FCC serializes the block bbox to six decimal places. One extra decimal of
// tolerance (~1.1 m latitude) prevents rounding at an exact block edge from
// rejecting the query point while remaining far below parcel-scale screening.
const FCC_BLOCK_BBOX_TOLERANCE_DEGREES = 0.00001;
const FCC_CENSUS_BLOCK_URL = 'https://geo.fcc.gov/api/census/block/find';
const JURISDICTION_CACHE_CONTRACT_VERSION = 'v3-calcasieu-hub-source';

export function isJurisdictionScreeningEnabled(): boolean {
  const viteEnv = typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env as Record<string, string | undefined>
    : undefined;
  const raw = viteEnv?.VITE_JURISDICTION_SCREENING_ENABLED
    ?? (typeof process !== 'undefined'
      ? process.env.JURISDICTION_SCREENING_ENABLED ?? process.env.VITE_JURISDICTION_SCREENING_ENABLED
      : undefined);
  if (typeof raw === 'string') return raw.trim().toLowerCase() === 'true';
  const mode = viteEnv?.MODE ?? (typeof process !== 'undefined' ? process.env.NODE_ENV : undefined);
  return mode === 'development' || mode === 'test';
}

/** Geometry-aware cache key: nearby but differently shaped parcels must not share results. */
export function jurisdictionCacheKey(context: ProviderCacheKeyContext): string {
  const parcels = context.geo?.boundaries?.length
    ? context.geo.boundaries
    : context.geo?.boundary
      ? [{ id: 'parcel-1', polygon: context.geo.boundary }]
      : [];
  const signatures = parcels
    .map(parcel => {
      const fingerprint = boundaryFingerprint(parcel.polygon);
      return fingerprint ? `${parcel.id}:${fingerprint}` : null;
    })
    .filter((signature): signature is string => signature != null)
    .sort();
  let hash = 0xcbf29ce484222325n;
  const composite = signatures.length > 0 ? signatures.join('|') : 'no-boundary';
  for (let i = 0; i < composite.length; i += 1) {
    hash ^= BigInt(composite.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  const geometryKey = hash.toString(16).padStart(16, '0');
  return `${JURISDICTION_CACHE_CONTRACT_VERSION}_${context.lat.toFixed(3)}_${context.lng.toFixed(3)}_${geometryKey}`;
}

function cleanRing(points: readonly number[][]): LngLat[] {
  const ring = points
    .filter(p => p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map(p => [Number(p[0]), Number(p[1])] as LngLat);
  if (ring.length >= 3) {
    const first = ring[0]!;
    const last = ring[ring.length - 1]!;
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first] as LngLat);
  }
  return ring;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

class ArcgisContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ArcgisContractError';
  }
}

function malformedArcgisResponse(layer: JurisdictionArcgisPolygonSource, detail: string): Error {
  return new ArcgisContractError(`${layer.sourceLabel}: malformed ArcGIS response (${detail})`);
}

function arcgisErrorDetail(value: unknown): string {
  if (!Array.isArray(value)) return '';
  const details = value
    .filter((detail): detail is string => typeof detail === 'string' && detail.trim() !== '')
    .map(detail => detail.trim());
  return details.length > 0 ? `; ${details.join('; ')}` : '';
}

/**
 * ArcGIS 4xx responses (other than rate limiting) indicate an invalid query,
 * retired layer, or other integration drift. They must not be downgraded to a
 * geographic fallback. Network errors, 429, and 5xx remain retryable.
 */
function assertArcgisHttpOk(
  response: Response,
  layer: JurisdictionArcgisPolygonSource,
): void {
  if (response.ok) return;
  if (response.status >= 400 && response.status < 500 && response.status !== 429) {
    throw new ArcgisContractError(
      `${layer.sourceLabel}: ArcGIS error HTTP ${response.status}`,
    );
  }
  if (response.status === 429) {
    throw new Error(`${layer.sourceLabel}: 429 ArcGIS upstream rate limit`);
  }
  throw new Error(`${layer.sourceLabel}: ArcGIS upstream HTTP ${response.status}`);
}

function parseArcgisRings(
  value: unknown,
  layer: JurisdictionArcgisPolygonSource,
  featureIndex: number,
): number[][][] {
  if (!Array.isArray(value) || value.length === 0) {
    throw malformedArcgisResponse(layer, `feature ${featureIndex + 1} has no polygon rings`);
  }

  return value.map((rawRing, ringIndex) => {
    if (!Array.isArray(rawRing) || rawRing.length < 4) {
      throw malformedArcgisResponse(
        layer,
        `feature ${featureIndex + 1} ring ${ringIndex + 1} has fewer than four positions`,
      );
    }
    const ring = rawRing.map((rawPoint, pointIndex) => {
      if (!Array.isArray(rawPoint)
        || rawPoint.length < 2
        || typeof rawPoint[0] !== 'number'
        || typeof rawPoint[1] !== 'number'
        || !Number.isFinite(rawPoint[0])
        || !Number.isFinite(rawPoint[1])
        || rawPoint[0] < -180
        || rawPoint[0] > 180
        || rawPoint[1] < -90
        || rawPoint[1] > 90) {
        throw malformedArcgisResponse(
          layer,
          `feature ${featureIndex + 1} ring ${ringIndex + 1} position ${pointIndex + 1} is invalid WGS84`,
        );
      }
      return [rawPoint[0], rawPoint[1]];
    });

    const first = ring[0]!;
    const last = ring[ring.length - 1]!;
    if (first[0] !== last[0] || first[1] !== last[1]) {
      throw malformedArcgisResponse(
        layer,
        `feature ${featureIndex + 1} ring ${ringIndex + 1} is not closed`,
      );
    }
    const distinctPositions = new Set(
      ring.slice(0, -1).map(([lng, lat]) => `${lng},${lat}`),
    );
    if (distinctPositions.size < 3 || Math.abs(signedRingArea(ring as LngLat[])) <= 1e-12) {
      throw malformedArcgisResponse(
        layer,
        `feature ${featureIndex + 1} ring ${ringIndex + 1} is degenerate`,
      );
    }
    return ring;
  });
}

function signedRingArea(ring: readonly LngLat[]): number {
  let sum = 0;
  for (let i = 0; i + 1 < ring.length; i += 1) {
    sum += ring[i]![0] * ring[i + 1]![1] - ring[i + 1]![0] * ring[i]![1];
  }
  return sum / 2;
}

function pointInRing(point: LngLat, ring: readonly LngLat[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]![0];
    const yi = ring[i]![1];
    const xj = ring[j]![0];
    const yj = ring[j]![1];
    if (((yi > point[1]) !== (yj > point[1]))
      && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** ArcGIS outer rings are clockwise; holes are counter-clockwise. */
export function esriRingsToMultiPolygon(ringsInput: readonly number[][][]): polygonClipping.MultiPolygon {
  const rings = ringsInput.map(cleanRing).filter(r => r.length >= 4);
  if (rings.length === 0) return [];

  let outers = rings.filter(r => signedRingArea(r) < 0);
  let holes = rings.filter(r => signedRingArea(r) >= 0);
  // Defensive fallback for services that ignore Esri winding conventions.
  if (outers.length === 0) {
    const sorted = [...rings].sort((a, b) => Math.abs(signedRingArea(b)) - Math.abs(signedRingArea(a)));
    outers = [sorted[0]!];
    holes = sorted.slice(1);
  }

  const polygons: polygonClipping.MultiPolygon = outers.map(outer => [outer]);
  for (const hole of holes) {
    const host = polygons.find(poly => pointInRing(hole[0]!, poly[0]!));
    if (host) host.push(hole);
    else polygons.push([hole]);
  }
  return polygons;
}

function parcelGeometry(ring: readonly LatLng[]): polygonClipping.MultiPolygon {
  const coords = cleanRing(ring.map(p => [p.lng, p.lat]));
  return coords.length >= 4 ? [[coords]] : [];
}

function multiPolygonAreaAcres(geometry: polygonClipping.MultiPolygon): number {
  let acres = 0;
  for (const polygon of geometry) {
    const outer = polygon[0];
    if (!outer) continue;
    acres += polygonArea(outer.map(([lng, lat]) => ({ lat, lng })));
    for (const hole of polygon.slice(1)) {
      acres -= polygonArea(hole.map(([lng, lat]) => ({ lat, lng })));
    }
  }
  return Math.max(0, acres);
}

function intersectionAreaAcres(
  parcel: polygonClipping.MultiPolygon,
  boundary: polygonClipping.MultiPolygon,
): number {
  if (parcel.length === 0 || boundary.length === 0) return 0;
  const intersection = polygonClipping.intersection(parcel, boundary) as polygonClipping.MultiPolygon;
  return multiPolygonAreaAcres(intersection);
}

function firstAttribute(attrs: Record<string, unknown>, fields: readonly string[]): string | null {
  for (const field of fields) {
    const raw = attrs[field];
    if (raw == null) continue;
    const value = String(raw).trim();
    if (value) return value;
  }
  return null;
}

function envelope(parcels: readonly ParcelInput[], fallback: LatLng): [number, number, number, number] {
  const points = parcels.flatMap(parcel => parcel.polygon);
  if (points.length === 0) return [fallback.lng, fallback.lat, fallback.lng, fallback.lat];
  return [
    Math.min(...points.map(p => p.lng)),
    Math.min(...points.map(p => p.lat)),
    Math.max(...points.map(p => p.lng)),
    Math.max(...points.map(p => p.lat)),
  ];
}

interface ParsedArcgisPolygonFeature {
  attributes: Record<string, unknown>;
  geometry: polygonClipping.MultiPolygon;
}

function parseArcgisPolygonResponse(
  payload: unknown,
  layer: JurisdictionArcgisPolygonSource,
): ParsedArcgisPolygonFeature[] {
  if (!isRecord(payload)) {
    throw malformedArcgisResponse(layer, 'top-level value is not an object');
  }
  if (payload.error != null) {
    if (!isRecord(payload.error)) {
      throw malformedArcgisResponse(layer, 'error envelope is not an object');
    }
    const error = payload.error;
    const code = error.code;
    const message = typeof error.message === 'string' && error.message.trim()
      ? error.message.trim()
      : 'unspecified upstream error';
    const detail = arcgisErrorDetail(error.details);
    const rendered = `${layer.sourceLabel}: ArcGIS error${
      typeof code === 'number' && Number.isFinite(code) ? ` ${code}` : ''
    }: ${message}${detail}`;
    if (typeof code !== 'number' || !Number.isFinite(code)) {
      throw malformedArcgisResponse(layer, `error envelope has invalid code; ${rendered}`);
    }
    if (code === 429) {
      throw new Error(`${layer.sourceLabel}: 429 ArcGIS upstream rate limit: ${message}${detail}`);
    }
    if (code >= 500) {
      throw new Error(`${layer.sourceLabel}: ArcGIS upstream ${code}: ${message}${detail}`);
    }
    throw new ArcgisContractError(rendered);
  }
  if ('exceededTransferLimit' in payload
    && typeof payload.exceededTransferLimit !== 'boolean') {
    throw malformedArcgisResponse(layer, 'exceededTransferLimit is not boolean');
  }
  if (payload.exceededTransferLimit === true) {
    throw new Error(`${layer.sourceLabel}: transfer limit exceeded; jurisdiction result is indeterminate`);
  }
  if (!Array.isArray(payload.features)) {
    throw malformedArcgisResponse(layer, 'features must be an array');
  }

  return payload.features.map((rawFeature, index) => {
    if (!isRecord(rawFeature)) {
      throw malformedArcgisResponse(layer, `feature ${index + 1} is not an object`);
    }
    if (!isRecord(rawFeature.attributes)) {
      throw malformedArcgisResponse(layer, `feature ${index + 1} attributes are missing or invalid`);
    }
    if (!isRecord(rawFeature.geometry)) {
      throw malformedArcgisResponse(layer, `feature ${index + 1} geometry is missing or invalid`);
    }
    const feature: ArcgisFeature = {
      attributes: rawFeature.attributes,
      geometry: {
        rings: parseArcgisRings(rawFeature.geometry.rings, layer, index),
      },
    };

    let geometry: polygonClipping.MultiPolygon;
    try {
      geometry = esriRingsToMultiPolygon(feature.geometry.rings);
    } catch {
      throw malformedArcgisResponse(layer, `feature ${index + 1} polygon topology is invalid`);
    }
    if (geometry.length === 0) {
      throw malformedArcgisResponse(layer, `feature ${index + 1} polygon is empty`);
    }
    return { attributes: feature.attributes, geometry };
  });
}

/**
 * Parse the polygon query contract as an all-or-nothing result. An explicit
 * `features: []` is a valid authoritative miss; a missing features array or
 * one malformed feature makes the layer unavailable instead of silently
 * turning schema drift into a false geographic negative.
 */
export function parseArcgisBoundaryResponse(
  payload: unknown,
  layer: JurisdictionArcgisLayer,
): JurisdictionBoundaryFeature[] {
  return parseArcgisPolygonResponse(payload, layer).map((feature, index) => {
    const id = firstAttribute(feature.attributes, layer.idFields);
    const name = firstAttribute(feature.attributes, layer.nameFields);
    if (!id) {
      throw malformedArcgisResponse(
        layer,
        `feature ${index + 1} has no configured jurisdiction identifier`,
      );
    }
    if (!name) {
      throw malformedArcgisResponse(
        layer,
        `feature ${index + 1} has no configured jurisdiction name`,
      );
    }
    return { id, name, kind: layer.layerKind, geometry: feature.geometry };
  });
}

/** Strictly parse the free FCC Census Block response used for registry scope. */
export function parseFccJurisdictionFips(
  payload: unknown,
  queryPoint: { lat: number; lng: number },
): ResolvedJurisdictionFips {
  if (!isRecord(payload)) throw new Error('FCC Census Block response is not an object');
  if (payload.status !== 'OK') {
    throw new Error('FCC Census Block response did not report status OK');
  }
  if (!isRecord(payload.Block) || !isRecord(payload.County) || !isRecord(payload.State)) {
    throw new Error('FCC Census Block response is missing Block, County, or State');
  }
  const blockFips = payload.Block.FIPS;
  const blockBbox = payload.Block.bbox;
  const countyFips = payload.County.FIPS;
  const stateFips = payload.State.FIPS;
  const countyName = payload.County.name;
  const stateCode = payload.State.code;
  if (typeof blockFips !== 'string' || !/^\d{15}$/.test(blockFips)
    || !Array.isArray(blockBbox) || blockBbox.length !== 4
    || !blockBbox.every(value => typeof value === 'number' && Number.isFinite(value))
    || typeof countyFips !== 'string' || !/^\d{5}$/.test(countyFips)
    || typeof stateFips !== 'string' || !/^\d{2}$/.test(stateFips)
    || typeof countyName !== 'string' || countyName.trim() === ''
    || typeof stateCode !== 'string' || !/^[A-Z]{2}$/.test(stateCode)) {
    throw new Error('FCC Census Block response contains invalid FIPS or jurisdiction fields');
  }
  if (!countyFips.startsWith(stateFips) || !blockFips.startsWith(countyFips)) {
    throw new Error('FCC Census Block response contains inconsistent FIPS fields');
  }
  const [west, south, east, north] = blockBbox as number[];
  if (west! < -180 || east! > 180 || south! < -90 || north! > 90
    || west! > east! || south! > north!) {
    throw new Error('FCC Census Block response contains an invalid Block.bbox');
  }
  if (!Number.isFinite(queryPoint.lat) || !Number.isFinite(queryPoint.lng)
    || queryPoint.lng < west! - FCC_BLOCK_BBOX_TOLERANCE_DEGREES
    || queryPoint.lng > east! + FCC_BLOCK_BBOX_TOLERANCE_DEGREES
    || queryPoint.lat < south! - FCC_BLOCK_BBOX_TOLERANCE_DEGREES
    || queryPoint.lat > north! + FCC_BLOCK_BBOX_TOLERANCE_DEGREES) {
    throw new Error('FCC Census Block response Block.bbox does not contain the query coordinate');
  }
  return {
    blockFips,
    blockBbox: [west!, south!, east!, north!],
    stateFips,
    countyFips: countyFips.slice(2),
    countyName: countyName.trim(),
    stateCode,
  };
}

/** FCC's published Block API requires an explicit censusYear. Keep the
 * request and recorded source URL identical, without rounding the point. */
export function fccJurisdictionLookupUrl(lat: number, lng: number): string {
  const params = new URLSearchParams({
    format: 'json',
    latitude: String(lat),
    longitude: String(lng),
    censusYear: '2020',
  });
  return `${FCC_CENSUS_BLOCK_URL}?${params.toString()}`;
}

/** Query the selected point at full precision; request actual county geometry. */
export function censusCountyJurisdictionLookupUrl(lat: number, lng: number): string {
  const params = new URLSearchParams({
    where: '1=1', geometry: `${lng},${lat}`, geometryType: 'esriGeometryPoint',
    inSR: '4326', outSR: '4326', spatialRel: 'esriSpatialRelIntersects',
    outFields: 'GEOID,STATE,COUNTY,NAME', returnGeometry: 'true', resultRecordCount: '2', f: 'json',
  });
  return `${CENSUS_COUNTIES_LAYER.queryUrl}?${params.toString()}`;
}

function assertJurisdictionPoint(point: LatLng): void {
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)
    || Math.abs(point.lat) > 90 || Math.abs(point.lng) > 180) throw new Error('Invalid jurisdiction query coordinate.');
}

/** Reuse the closed ArcGIS polygon parser, then independently contain the point. */
export function parseCensusCountyPointResponse(payload: unknown, point: LatLng): ResolvedCensusCountyFips {
  assertJurisdictionPoint(point);
  const features = parseArcgisPolygonResponse(payload, CENSUS_COUNTIES_LAYER);
  if (!isRecord(payload) || !isRecord(payload.spatialReference) || payload.spatialReference.wkid !== 4326) {
    throw new Error('Census county response must declare WGS84 coordinates.');
  }
  if (features.length !== 1) throw new Error(`Census county point query expected one county, received ${features.length}.`);
  const feature = features[0]!;
  const { GEOID: geoid, STATE: stateFips, COUNTY: countyFips, NAME: name } = feature.attributes;
  const stateCode = typeof stateFips === 'string' ? statePostalCodeForFips(stateFips) : null;
  if (typeof geoid !== 'string' || !/^\d{5}$/.test(geoid)
    || typeof stateFips !== 'string' || !/^\d{2}$/.test(stateFips)
    || typeof countyFips !== 'string' || !/^\d{3}$/.test(countyFips)
    || geoid !== stateFips + countyFips || !stateCode || typeof name !== 'string' || !name.trim()) {
    throw new Error('Census county response contains invalid or inconsistent FIPS/name fields.');
  }
  const query: LngLat = [point.lng, point.lat];
  if (!feature.geometry.some(polygon => pointInRing(query, polygon[0]!)
    && polygon.slice(1).every(hole => !pointInRing(query, hole)))) {
    throw new Error('Census county polygon does not contain the query coordinate.');
  }
  // Retain validated source rings so later archive routing can repeat containment.
  const source = (payload.features as Array<{ geometry: { rings: number[][][] } }>)[0]!;
  return { stateFips, countyFips, countyName: name.trim(), stateCode,
    sourceUrl: censusCountyJurisdictionLookupUrl(point.lat, point.lng),
    censusCounty: { rings: source.geometry.rings.map(ring => ring.map(position => [...position])) } };
}

/** Revalidate a saved county receipt without converting county geometry into a block. */
export function parseResolvedCountyEvidence(value: unknown, point: LatLng, sourceUrl?: string): ResolvedCountyJurisdictionFips {
  if (!isRecord(value)) throw new Error('Saved county evidence is invalid.');
  if ('censusCounty' in value) {
    if (!isRecord(value.censusCounty) || 'blockFips' in value || 'blockBbox' in value
      || sourceUrl !== censusCountyJurisdictionLookupUrl(point.lat, point.lng) || value.sourceUrl !== sourceUrl) {
      throw new Error('Saved Census county evidence has invalid source or mixed block authority.');
    }
    const result = parseCensusCountyPointResponse({ spatialReference: { wkid: 4326 }, features: [{
      attributes: { GEOID: `${value.stateFips}${value.countyFips}`, STATE: value.stateFips, COUNTY: value.countyFips, NAME: value.countyName },
      geometry: { rings: value.censusCounty.rings },
    }] }, point);
    if (result.stateCode !== value.stateCode || result.countyName !== value.countyName) throw new Error('Saved Census county identity changed.');
    return result;
  }
  return parseFccJurisdictionFips({ status: 'OK', Block: { FIPS: value.blockFips, bbox: value.blockBbox },
    County: { FIPS: `${value.stateFips}${value.countyFips}`, name: value.countyName }, State: { FIPS: value.stateFips, code: value.stateCode } }, point);
}

export function resolveJurisdictionFips(lat: number, lng: number, signal?: AbortSignal): Promise<ResolvedJurisdictionFips>;
export function resolveJurisdictionFips(lat: number, lng: number, signal: AbortSignal | undefined,
  options: { countyFallback: true }): Promise<ResolvedCountyJurisdictionFips>;
export async function resolveJurisdictionFips(lat: number, lng: number, signal?: AbortSignal,
  options?: { countyFallback: true }): Promise<ResolvedCountyJurisdictionFips> {
  throwIfAborted(signal);
  assertJurisdictionPoint({ lat, lng });
  try {
    const res = await fetchWithTimeout(fccJurisdictionLookupUrl(lat, lng), 15_000, signal);
    if (!res.ok) throw new Error(`FCC Census Block API: HTTP ${res.status}`);
    const body: unknown = await res.json();
    throwIfAborted(signal);
    return parseFccJurisdictionFips(body, { lat, lng });
  } catch (fccError) {
    rethrowIfAborted(signal, fccError);
    if (!options?.countyFallback) throw fccError; // Parcel/ETJ callers retain their original block contract.
    try {
      const res = await fetchWithTimeout(censusCountyJurisdictionLookupUrl(lat, lng), 15_000, signal);
      assertArcgisHttpOk(res, CENSUS_COUNTIES_LAYER);
      const body: unknown = await res.json();
      throwIfAborted(signal);
      return parseCensusCountyPointResponse(body, { lat, lng });
    } catch (censusError) {
      rethrowIfAborted(signal, censusError);
      throw new Error(`${fccError instanceof Error ? fccError.message : String(fccError)}; Census county fallback unavailable: ${
        censusError instanceof Error ? censusError.message : String(censusError)}`);
    }
  }
}

/** County-only consumers may use independently contained Census polygons. */
export function resolveCountyJurisdictionFips(lat: number, lng: number, signal?: AbortSignal): Promise<ResolvedCountyJurisdictionFips> {
  return resolveJurisdictionFips(lat, lng, signal, { countyFallback: true });
}

interface CountyBoundary {
  geoid: string;
  name: string;
  geometry: polygonClipping.MultiPolygon;
}

export function parseArcgisCountyBoundaryResponse(
  payload: unknown,
  expectedGeoid: string,
): CountyBoundary | null {
  const features = parseArcgisPolygonResponse(payload, CENSUS_COUNTIES_LAYER);
  if (features.length === 0) return null;
  if (features.length !== 1) {
    throw malformedArcgisResponse(
      CENSUS_COUNTIES_LAYER,
      `expected one county feature for GEOID ${expectedGeoid}, received ${features.length}`,
    );
  }
  const feature = features[0]!;
  const geoid = firstAttribute(feature.attributes, CENSUS_COUNTIES_LAYER.idFields);
  if (geoid !== expectedGeoid) {
    throw malformedArcgisResponse(
      CENSUS_COUNTIES_LAYER,
      `county GEOID ${geoid ?? 'missing'} does not match expected ${expectedGeoid}`,
    );
  }
  const name = firstAttribute(feature.attributes, CENSUS_COUNTIES_LAYER.nameFields);
  if (!name) {
    throw malformedArcgisResponse(
      CENSUS_COUNTIES_LAYER,
      `county GEOID ${geoid} has no configured county name`,
    );
  }
  return {
    geoid,
    name,
    geometry: feature.geometry,
  };
}

async function queryCountyBoundary(
  registry: JurisdictionRegistryEntry,
  bbox: [number, number, number, number],
  signal?: AbortSignal,
): Promise<CountyBoundary | null> {
  const expectedGeoid = `${registry.stateFips}${registry.countyFips}`;
  const params = new URLSearchParams({
    where: `GEOID='${expectedGeoid}'`,
    geometry: bbox.join(','),
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: [...new Set([
      ...CENSUS_COUNTIES_LAYER.nameFields,
      ...CENSUS_COUNTIES_LAYER.idFields,
    ])].join(','),
    returnGeometry: 'true',
    resultRecordCount: '2',
    f: 'json',
  });
  const res = await fetchWithTimeout(
    `${CENSUS_COUNTIES_LAYER.queryUrl}?${params.toString()}`,
    25_000,
    signal,
  );
  assertArcgisHttpOk(res, CENSUS_COUNTIES_LAYER);
  return parseArcgisCountyBoundaryResponse(await res.json() as unknown, expectedGeoid);
}

interface CountyContainmentResult {
  contained: boolean;
  outsideParcelIds: string[];
}

function validateCountyContainment(
  parcels: readonly ParcelInput[],
  county: CountyBoundary,
): CountyContainmentResult {
  const outsideParcelIds: string[] = [];
  for (const parcel of parcels) {
    const geometry = parcelGeometry(parcel.polygon);
    const parcelAcres = multiPolygonAreaAcres(geometry);
    if (geometry.length === 0 || parcelAcres <= 0) {
      throw new Error(`Parcel ${parcel.id} geometry is invalid for county-containment validation`);
    }
    const outside = polygonClipping.difference(
      geometry,
      county.geometry,
    ) as polygonClipping.MultiPolygon;
    const outsideAcres = multiPolygonAreaAcres(outside);
    const toleranceAcres = Math.max(
      COUNTY_CONTAINMENT_ABSOLUTE_TOLERANCE_ACRES,
      parcelAcres * COUNTY_CONTAINMENT_RELATIVE_TOLERANCE,
    );
    if (outsideAcres > toleranceAcres) outsideParcelIds.push(parcel.id);
  }
  return { contained: outsideParcelIds.length === 0, outsideParcelIds };
}

function assertValidParcels(parcels: readonly ParcelInput[]): void {
  const ids = new Set<string>();
  for (const parcel of parcels) {
    if (typeof parcel.id !== 'string' || parcel.id.trim() === '' || ids.has(parcel.id)) {
      throw new Error('Jurisdiction screening requires unique non-empty parcel IDs');
    }
    ids.add(parcel.id);
    if (parcel.polygon.some(point => (
      !Number.isFinite(point.lat)
      || !Number.isFinite(point.lng)
      || point.lat < -90
      || point.lat > 90
      || point.lng < -180
      || point.lng > 180
    ))) {
      throw new Error(`Parcel ${parcel.id} contains an invalid WGS84 coordinate`);
    }
    const uniquePoints = new Set(parcel.polygon.map(point => `${point.lat},${point.lng}`));
    const geometry = parcelGeometry(parcel.polygon);
    if (uniquePoints.size < 3 || geometry.length === 0 || multiPolygonAreaAcres(geometry) <= 0) {
      throw new Error(`Parcel ${parcel.id} geometry is degenerate`);
    }
  }
}

interface RegistrySelection {
  registry: JurisdictionRegistryEntry | null;
  verifiedFips?: ResolvedJurisdictionFips;
  verificationAttempted: boolean;
  transientValidationFailure: boolean;
  countyBoundary?: CountyBoundary;
  applicability: JurisdictionRegistryApplicabilityEvidence;
  warnings: string[];
}

async function selectJurisdictionRegistry(
  lat: number,
  lng: number,
  parcels: readonly ParcelInput[],
  signal?: AbortSignal,
): Promise<RegistrySelection> {
  const candidate = findJurisdictionRegistryCandidate(lat, lng);
  const checkedAt = new Date().toISOString();
  const evidenceBase = {
    method: 'fcc-census-block+us-census-county-polygon' as const,
    checkedAt,
    queryPoint: { lat, lng },
    ...(candidate
      ? {
          expectedRegistry: {
            registryId: candidate.id,
            stateFips: candidate.stateFips,
            countyFips: candidate.countyFips,
          },
        }
      : {}),
  };
  const noCountyCheck = {
    status: 'not-run' as const,
    sourceUrl: CENSUS_COUNTIES_LAYER.sourceUrl,
  };
  if (!candidate) {
    return {
      registry: null,
      verificationAttempted: false,
      transientValidationFailure: false,
      applicability: {
        ...evidenceBase,
        status: 'not-required',
        fcc: { status: 'not-run', sourceUrl: FCC_CENSUS_BLOCK_URL },
        countyContainment: noCountyCheck,
      },
      warnings: [],
    };
  }

  let verifiedFips: ResolvedJurisdictionFips | undefined;
  let countyValidationAttempted = false;
  try {
    verifiedFips = await resolveJurisdictionFips(lat, lng, signal);
    const fccEvidence = {
      status: 'verified' as const,
      sourceUrl: FCC_CENSUS_BLOCK_URL,
      blockFips: verifiedFips.blockFips,
      blockBbox: verifiedFips.blockBbox,
      stateFips: verifiedFips.stateFips,
      countyFips: verifiedFips.countyFips,
      countyName: verifiedFips.countyName,
      stateCode: verifiedFips.stateCode,
    };
    const registry = findJurisdictionRegistryEntry(lat, lng, verifiedFips);
    if (!registry) {
      return {
        registry: null,
        verifiedFips,
        verificationAttempted: true,
        transientValidationFailure: false,
        applicability: {
          ...evidenceBase,
          status: 'mismatch',
          fcc: { ...fccEvidence, status: 'mismatch' },
          countyContainment: noCountyCheck,
        },
        warnings: [
          `Curated jurisdiction registry ${candidate.id} was not applied because independently resolved county FIPS ${verifiedFips.stateFips}:${verifiedFips.countyFips} does not match ${candidate.stateFips}:${candidate.countyFips}; Census incorporated-place fallback used.`,
        ],
      };
    }

    countyValidationAttempted = true;
    const countyBoundary = await queryCountyBoundary(
      registry,
      envelope(parcels, { lat, lng }),
      signal,
    );
    if (!countyBoundary) {
      return {
        registry: null,
        verifiedFips,
        verificationAttempted: true,
        transientValidationFailure: false,
        applicability: {
          ...evidenceBase,
          status: 'mismatch',
          fcc: fccEvidence,
          countyContainment: {
            status: 'not-intersecting',
            sourceUrl: CENSUS_COUNTIES_LAYER.sourceUrl,
          },
        },
        warnings: [
          `Curated jurisdiction registry ${candidate.id} was not applied because the independently queried Census county polygon did not intersect the submitted parcels; Census incorporated-place fallback used.`,
        ],
      };
    }
    const containment = validateCountyContainment(parcels, countyBoundary);
    if (!containment.contained) {
      return {
        registry: null,
        verifiedFips,
        verificationAttempted: true,
        transientValidationFailure: false,
        countyBoundary,
        applicability: {
          ...evidenceBase,
          status: 'cross-county',
          fcc: fccEvidence,
          countyContainment: {
            status: 'cross-county',
            sourceUrl: CENSUS_COUNTIES_LAYER.sourceUrl,
            geoid: countyBoundary.geoid,
            outsideParcelIds: containment.outsideParcelIds,
          },
        },
        warnings: [
          `Curated jurisdiction registry ${candidate.id} was not applied because parcel(s) ${containment.outsideParcelIds.join(', ')} are not wholly contained in Census county GEOID ${countyBoundary.geoid}; Census incorporated-place fallback used for every parcel.`,
        ],
      };
    }
    return {
      registry,
      verifiedFips,
      verificationAttempted: true,
      transientValidationFailure: false,
      countyBoundary,
      applicability: {
        ...evidenceBase,
        status: 'verified',
        fcc: fccEvidence,
        countyContainment: {
          status: 'verified',
          sourceUrl: CENSUS_COUNTIES_LAYER.sourceUrl,
          geoid: countyBoundary.geoid,
        },
      },
      warnings: [],
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    if (error instanceof ArcgisContractError) throw error;
    const detail = error instanceof Error ? error.message : String(error);
    return {
      registry: null,
      verificationAttempted: true,
      transientValidationFailure: true,
      applicability: {
        ...evidenceBase,
        status: 'unavailable',
        fcc: verifiedFips
          ? {
              status: 'verified',
              sourceUrl: FCC_CENSUS_BLOCK_URL,
              blockFips: verifiedFips.blockFips,
              blockBbox: verifiedFips.blockBbox,
              stateFips: verifiedFips.stateFips,
              countyFips: verifiedFips.countyFips,
              countyName: verifiedFips.countyName,
              stateCode: verifiedFips.stateCode,
            }
          : { status: 'unavailable', sourceUrl: FCC_CENSUS_BLOCK_URL, error: detail },
        countyContainment: countyValidationAttempted
          ? { status: 'unavailable', sourceUrl: CENSUS_COUNTIES_LAYER.sourceUrl, error: detail }
          : noCountyCheck,
      },
      warnings: [
        `Curated jurisdiction registry ${candidate.id} was not applied because county applicability could not be independently verified (${detail}); Census incorporated-place fallback used without long-term caching.`,
      ],
    };
  }
}

async function queryLayer(
  layer: JurisdictionArcgisLayer,
  bbox: [number, number, number, number],
  signal?: AbortSignal,
): Promise<JurisdictionBoundaryFeature[]> {
  const fields = [...new Set([...layer.nameFields, ...layer.idFields])];
  const pointQuery = bbox[0] === bbox[2] && bbox[1] === bbox[3];
  const params = new URLSearchParams({
    where: '1=1',
    geometry: pointQuery ? `${bbox[0]},${bbox[1]}` : bbox.join(','),
    geometryType: pointQuery ? 'esriGeometryPoint' : 'esriGeometryEnvelope',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: fields.join(','),
    returnGeometry: 'true',
    resultRecordCount: '2000',
    f: 'json',
  });
  const res = await fetchWithTimeout(`${layer.queryUrl}?${params.toString()}`, 25_000, signal);
  assertArcgisHttpOk(res, layer);
  return parseArcgisBoundaryResponse(await res.json() as unknown, layer);
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

function parcelStatus(
  parcelAcres: number,
  overlaps: readonly JurisdictionOverlapEvidence[],
  etjComplete: boolean,
): JurisdictionScreeningStatus {
  const cities = overlaps.filter(o => o.kind === 'city-limit');
  const etjs = overlaps.filter(o => o.kind === 'etj');
  const cityNames = unique(cities.map(o => o.name));
  const etjNames = unique(etjs.map(o => o.name));
  const cityFraction = Math.min(1, cities.reduce((sum, o) => sum + o.acres, 0) / Math.max(parcelAcres, 1e-9));
  const etjFraction = Math.min(1, etjs.reduce((sum, o) => sum + o.acres, 0) / Math.max(parcelAcres, 1e-9));
  if (cityNames.length > 1 || etjNames.length > 1) return 'mixed';
  if (cityFraction > AREA_EPSILON_FRACTION) {
    return cityFraction >= 1 - AREA_EPSILON_FRACTION ? 'inside-city-limits' : 'mixed';
  }
  if (etjFraction > AREA_EPSILON_FRACTION) {
    return etjFraction >= 1 - AREA_EPSILON_FRACTION ? 'inside-etj' : 'mixed';
  }
  return etjComplete ? 'outside-known-etj' : 'unknown';
}

export function classifyJurisdiction(
  parcels: readonly ParcelInput[],
  features: readonly JurisdictionBoundaryFeature[],
  options: {
    registryId: string;
    registryApplicability?: JurisdictionRegistryApplicabilityEvidence;
    registryCoverage: JurisdictionEvidence['registryCoverage'];
    stateFips?: string;
    countyFips?: string;
    datasetEffectiveDate?: string;
    lastReviewedAt: string;
    warnings?: string[];
    authorityReferences?: string[];
    sources?: JurisdictionEvidence['sources'];
  },
): JurisdictionEvidence {
  const etjComplete = options.registryCoverage === 'complete';
  const parcelResults: ParcelJurisdictionEvidence[] = parcels.map(parcel => {
    const geom = parcelGeometry(parcel.polygon);
    const acres = multiPolygonAreaAcres(geom);
    const overlaps = features.flatMap(feature => {
      const overlapAcres = intersectionAreaAcres(geom, feature.geometry);
      if (overlapAcres <= Math.max(1e-6, acres * AREA_EPSILON_FRACTION)) return [];
      return [{
        name: feature.name,
        kind: feature.kind,
        acres: overlapAcres,
        fraction: Math.min(1, overlapAcres / Math.max(acres, 1e-9)),
        sourceId: feature.id,
      } satisfies JurisdictionOverlapEvidence];
    }).sort((a, b) => b.acres - a.acres);
    return {
      parcelId: parcel.id,
      parcelAcres: acres,
      overlaps,
      status: parcelStatus(acres, overlaps, etjComplete),
    };
  });

  const statuses = unique(parcelResults.map(p => p.status));
  const municipalityNames = unique(parcelResults.flatMap(p => p.overlaps
    .filter(o => o.kind === 'city-limit').map(o => o.name)));
  const etjNames = unique(parcelResults.flatMap(p => p.overlaps
    .filter(o => o.kind === 'etj').map(o => o.name)));
  const hasNamedConflict = municipalityNames.length > 1 || etjNames.length > 1;
  const hasParcelDisagreement = statuses.length > 1;
  const status: JurisdictionScreeningStatus = hasNamedConflict || hasParcelDisagreement
    ? 'mixed'
    : parcelResults[0]?.status ?? 'unknown';
  const conflictWarnings = [
    ...(hasNamedConflict
      ? ['Multiple named jurisdiction polygons intersect the submitted parcels; the result remains mixed.']
      : []),
    ...(hasParcelDisagreement
      ? ['Submitted parcels have different jurisdiction classifications; the combined result remains mixed.']
      : []),
  ];

  return {
    status,
    classification: status,
    insideCityLimits: status === 'inside-city-limits'
      ? true
      : status === 'mixed' ? null : false,
    insideEtj: status === 'inside-etj'
      ? true
      : status === 'outside-known-etj' ? false : null,
    municipalities: municipalityNames,
    etjAuthorities: etjNames,
    governingJurisdiction: municipalityNames[0] ?? etjNames[0] ?? null,
    candidates: unique([...municipalityNames, ...etjNames]),
    authorityScope: 'not-evaluated',
    authorityNotes: 'Geographic polygon screening only; permitting authority was not evaluated.',
    registryId: options.registryId,
    ...(options.registryApplicability
      ? { registryApplicability: options.registryApplicability }
      : {}),
    ...(options.stateFips ? { stateFips: options.stateFips } : {}),
    ...(options.countyFips ? { countyFips: options.countyFips } : {}),
    registryCoverage: options.registryCoverage,
    ...(options.datasetEffectiveDate ? { datasetEffectiveDate: options.datasetEffectiveDate } : {}),
    lastReviewedAt: options.lastReviewedAt,
    authorityReferences: options.authorityReferences ?? [],
    sources: options.sources ?? [],
    parcels: parcelResults,
    perParcel: parcelResults.map(parcel => ({
      parcelId: parcel.parcelId,
      classification: parcel.status,
      insideCityLimits: parcel.status === 'inside-city-limits'
        ? true
        : parcel.status === 'mixed' ? null : false,
      insideEtj: parcel.status === 'inside-etj'
        ? true
        : parcel.status === 'outside-known-etj' ? false : null,
    })),
    warnings: [...(options.warnings ?? []), ...conflictWarnings],
  };
}

export const jurisdictionProvider: DataProvider<JurisdictionEvidence> = {
  id: 'jurisdiction-boundaries',
  name: 'Municipal Limits and ETJ Screening',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,
  cacheKey: jurisdictionCacheKey,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<JurisdictionEvidence>> {
    if (!isJurisdictionScreeningEnabled()) throw new Error('jurisdiction_screening_disabled');
    const canonical = geo?.boundaries?.filter(p => p.polygon.length >= 3) ?? [];
    const parcels: ParcelInput[] = canonical.length > 0
      ? canonical
      : geo?.boundary && geo.boundary.length >= 3
        ? [{ id: 'parcel-1', polygon: geo.boundary }]
        : [];
    if (parcels.length === 0) {
      throw new Error('Jurisdiction screening requires a parcel boundary for exact overlap analysis');
    }
    assertValidParcels(parcels);

    const selection = await selectJurisdictionRegistry(lat, lng, parcels, geo?.signal);
    const registry = selection.registry;
    const layers = registry?.layers ?? [CENSUS_INCORPORATED_PLACES_LAYER];
    const bbox = envelope(parcels, { lat, lng });
    const featureGroups = await Promise.all(layers.map(layer => queryLayer(layer, bbox, geo?.signal)));
    const features = featureGroups.flat();
    const retrievedAt = new Date().toISOString();
    const data = classifyJurisdiction(parcels, features, registry ? {
      registryId: registry.id,
      registryApplicability: selection.applicability,
      stateFips: registry.stateFips,
      countyFips: registry.countyFips,
      registryCoverage: registry.etjCoverage,
      datasetEffectiveDate: registry.datasetEffectiveDate,
      lastReviewedAt: registry.lastReviewedAt,
      authorityReferences: [...registry.authorityReferences, CENSUS_COUNTIES_LAYER.sourceUrl],
      sources: [
        ...layers.map(layer => ({
          label: layer.sourceLabel,
          providerId: layer.providerId,
          kind: layer.layerKind,
          url: layer.sourceUrl,
          retrievedAt,
          ...(registry.datasetEffectiveDate
            ? {
                datasetEffectiveDate: registry.datasetEffectiveDate,
                dataAsOf: registry.datasetEffectiveDate,
              }
            : {}),
        })),
        {
          label: CENSUS_COUNTIES_LAYER.sourceLabel,
          providerId: CENSUS_COUNTIES_LAYER.providerId,
          kind: 'county-boundary' as const,
          url: CENSUS_COUNTIES_LAYER.sourceUrl,
          retrievedAt,
          datasetEffectiveDate: '2025-01-01',
          dataAsOf: '2025-01-01',
        },
      ],
      warnings: registry.etjCoverage === 'not-published'
        ? [
            'No authoritative ETJ polygon is published in the configured registry; ETJ status remains unknown.',
            ...selection.warnings,
          ]
        : selection.warnings,
    } : {
      registryId: 'us-census-incorporated-places-fallback',
      registryApplicability: selection.applicability,
      registryCoverage: 'fallback',
      lastReviewedAt: '2026-07-13',
      authorityReferences: [CENSUS_INCORPORATED_PLACES_LAYER.sourceUrl],
      sources: [
        ...layers.map(layer => ({
          label: layer.sourceLabel,
          providerId: layer.providerId,
          kind: 'census-place' as const,
          url: layer.sourceUrl,
          retrievedAt,
          datasetEffectiveDate: '2025',
          dataAsOf: '2025',
        })),
        ...(selection.countyBoundary
          ? [{
              label: CENSUS_COUNTIES_LAYER.sourceLabel,
              providerId: CENSUS_COUNTIES_LAYER.providerId,
              kind: 'county-boundary' as const,
              url: CENSUS_COUNTIES_LAYER.sourceUrl,
              retrievedAt,
              datasetEffectiveDate: '2025-01-01',
              dataAsOf: '2025-01-01',
            }]
          : []),
      ],
      warnings: [
        'Census incorporated-place boundaries screen city limits only; ETJ status remains unknown.',
        ...selection.warnings,
      ],
    });

    return {
      data,
      ...(selection.transientValidationFailure ? { cacheTtlMs: 0 } : {}),
      sources: [
        ...layers.map(layer => ({
          field: 'regulatory.jurisdiction',
          source: `${layer.sourceLabel}; geographic screening only, permitting authority not evaluated`,
          url: layer.sourceUrl,
          retrievedAt,
        })),
        ...(selection.verificationAttempted
          ? [{
              field: 'regulatory.jurisdiction.registryApplicability',
              source: selection.verifiedFips
                ? `FCC Census Block API county FIPS validation (${selection.verifiedFips.stateFips}${selection.verifiedFips.countyFips})`
                : 'FCC Census Block API county FIPS validation unavailable; curated registry not applied',
              url: FCC_CENSUS_BLOCK_URL,
              retrievedAt,
            }]
          : []),
        ...(selection.countyBoundary
          ? [{
              field: 'regulatory.jurisdiction.registryApplicability',
              source: `${CENSUS_COUNTIES_LAYER.sourceLabel} exact parcel-containment validation (${selection.countyBoundary.geoid})`,
              url: CENSUS_COUNTIES_LAYER.sourceUrl,
              retrievedAt,
            }]
          : []),
      ],
    };
  },
};
